/**
 * Anmeldung über MCPAuth.
 *
 * Authorization Code Flow mit PKCE, wie ihn die übrigen Projekte nutzen. Der
 * Client ist öffentlich registriert und hat kein Geheimnis — die Sicherheit
 * hängt am `code_verifier`, der den Browser nie verlässt.
 *
 * Die Token liegen im sessionStorage statt im localStorage: sie überleben ein
 * Neuladen der Seite, aber nicht das Schließen des Tabs.
 */

const ISSUER = "https://auth.syscowa.app";
const CLIENT_ID = "timeline";
const SCOPE = "openid profile email offline_access";
const ABLAGE = "ribbon.sitzung";

export interface Benutzer {
  sub: string;
  kuerzel: string;
  name: string;
  email?: string;
  role: string;
}

export interface Sitzung {
  accessToken: string;
  refreshToken?: string;
  idToken?: string;
  laeuftAbUm: number;
  benutzer: Benutzer;
}

let aktuell: Sitzung | null = null;

function rueckleitung(): string {
  return `${window.location.origin}/auth/callback`;
}

function base64url(bytes: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function zufall(): string {
  return base64url(crypto.getRandomValues(new Uint8Array(32)).buffer);
}

async function herausforderung(verifier: string): Promise<string> {
  const abdruck = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64url(abdruck);
}

export function laden(): Sitzung | null {
  if (aktuell) return aktuell;

  const roh = sessionStorage.getItem(ABLAGE);
  if (!roh) return null;

  try {
    aktuell = JSON.parse(roh) as Sitzung;
    return aktuell;
  } catch {
    sessionStorage.removeItem(ABLAGE);
    return null;
  }
}

function ablegen(sitzung: Sitzung): void {
  aktuell = sitzung;
  sessionStorage.setItem(ABLAGE, JSON.stringify(sitzung));
}

export function verwerfen(): void {
  aktuell = null;
  sessionStorage.removeItem(ABLAGE);
}

export function zugriffstoken(): string | null {
  return laden()?.accessToken ?? null;
}

/**
 * Nutzlast eines JWT — nur zum Anzeigen, geprüft wird im Backend. Als UTF-8
 * dekodiert, sonst würden Umlaute im Namen verstümmelt.
 */
function nutzlast(jwt: string | undefined): Record<string, unknown> {
  try {
    const teil = String(jwt).split(".")[1]!.replace(/-/g, "+").replace(/_/g, "/");
    const bytes = Uint8Array.from(atob(teil), (z) => z.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/**
 * Die Rolle kommt aus dem Zugriffstoken, nicht aus dem ID-Token: MCPAuth
 * schreibt sie nur dort hinein (das ID-Token trägt `sub`, `name`, `kuerzel`,
 * `email`). Aus dem ID-Token gelesen stand hier für jeden „viewer" — und alles,
 * was an `admin` hing, blieb unsichtbar, obwohl Encom es zugelassen hätte.
 * Bei jedem Aufruf neu gelesen, damit sie auch nach einer Erneuerung stimmt.
 */
export function benutzer(): Benutzer | null {
  const sitzung = laden();
  if (!sitzung) return null;
  const rolle = nutzlast(sitzung.accessToken).role;
  return { ...sitzung.benutzer, role: typeof rolle === "string" ? rolle : sitzung.benutzer.role };
}

/** Zum Anmeldeformular von MCPAuth wechseln. */
export async function anmelden(): Promise<void> {
  const zustand = zufall();
  const verifier = zufall();

  sessionStorage.setItem("ribbon.zustand", zustand);
  sessionStorage.setItem("ribbon.verifier", verifier);
  // Wohin es nach der Anmeldung zurückgehen soll.
  sessionStorage.setItem("ribbon.zurueck", window.location.pathname + window.location.search);

  const felder = new URLSearchParams({
    response_type: "code",
    client_id: CLIENT_ID,
    redirect_uri: rueckleitung(),
    scope: SCOPE,
    state: zustand,
    code_challenge: await herausforderung(verifier),
    code_challenge_method: "S256",
  });

  window.location.href = `${ISSUER}/oauth2/auth?${felder}`;
}

/** Rückkehr von MCPAuth verarbeiten. Gibt das Ziel zurück, zu dem es weitergeht. */
export async function rueckkehrVerarbeiten(): Promise<string> {
  const felder = new URLSearchParams(window.location.search);

  const fehler = felder.get("error");
  if (fehler) {
    throw new Error(`${fehler}: ${felder.get("error_description") ?? "unbekannt"}`);
  }

  const code = felder.get("code");
  const zustand = felder.get("state");
  const erwartet = sessionStorage.getItem("ribbon.zustand");
  const verifier = sessionStorage.getItem("ribbon.verifier");

  // Ohne diesen Vergleich könnte eine fremde Seite den Browser mit einem
  // untergeschobenen Code hierher schicken.
  if (!code || !zustand || zustand !== erwartet || !verifier) {
    throw new Error("Antwort passt nicht zur gestellten Anfrage");
  }

  const antwort = await fetch(`${ISSUER}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      code,
      redirect_uri: rueckleitung(),
      client_id: CLIENT_ID,
      code_verifier: verifier,
    }),
  });

  if (!antwort.ok) throw new Error(`Token-Tausch fehlgeschlagen: ${await antwort.text()}`);

  const token = await antwort.json();
  const claims = { ...nutzlast(token.id_token), ...nutzlast(token.access_token) } as Record<string, string | undefined>;

  ablegen({
    accessToken: token.access_token,
    refreshToken: token.refresh_token,
    idToken: token.id_token,
    laeuftAbUm: Date.now() + (token.expires_in ?? 900) * 1000,
    benutzer: {
      sub: claims.sub ?? "",
      kuerzel: claims.kuerzel ?? "",
      name: claims.name ?? claims.kuerzel ?? "unbekannt",
      email: claims.email,
      role: claims.role ?? "viewer",
    },
  });

  const ziel = sessionStorage.getItem("ribbon.zurueck") || "/";
  sessionStorage.removeItem("ribbon.zustand");
  sessionStorage.removeItem("ribbon.verifier");
  sessionStorage.removeItem("ribbon.zurueck");
  return ziel;
}

/**
 * Zugriffstoken erneuern, bevor es abläuft.
 *
 * MCPAuth stellt Zugriffstoken für 15 Minuten aus. Wer eine Weile durch die
 * Zeitachse blättert, müsste sich sonst ständig neu anmelden.
 */
export async function erneuernFallsNoetig(): Promise<boolean> {
  const sitzung = laden();
  if (!sitzung) return false;
  if (Date.now() < sitzung.laeuftAbUm - 60_000) return true;
  if (!sitzung.refreshToken) return false;

  const antwort = await fetch(`${ISSUER}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "refresh_token",
      refresh_token: sitzung.refreshToken,
      client_id: CLIENT_ID,
    }),
  });

  if (!antwort.ok) {
    verwerfen();
    return false;
  }

  const token = await antwort.json();
  ablegen({
    ...sitzung,
    accessToken: token.access_token,
    refreshToken: token.refresh_token ?? sitzung.refreshToken,
    laeuftAbUm: Date.now() + (token.expires_in ?? 900) * 1000,
  });
  return true;
}

export function abmelden(): void {
  const sitzung = laden();
  verwerfen();

  if (sitzung?.idToken) {
    const felder = new URLSearchParams({
      id_token_hint: sitzung.idToken,
      post_logout_redirect_uri: `${window.location.origin}/`,
    });
    window.location.href = `${ISSUER}/oauth2/session/end?${felder}`;
  } else {
    window.location.href = "/";
  }
}
